import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setPreViewFilters } from '../redux/slices/views';

/**
 * Defines a single filter managed by useUrlFilters.
 *
 * @property urlKey      – query-param key (e.g. "type", "status")
 * @property storageKey  – localStorage persistence key
 * @property defaultValue – value when neither URL nor localStorage has one
 * @property multi       – true ⟹ comma-separated array param (?status=a,b,c)
 */
export interface FilterDef {
  urlKey: string;
  storageKey: string;
  defaultValue: string;
  multi?: boolean;
}

type FilterValues = Record<string, string | string[]>;

/**
 * Reusable hook that keeps filter state in URL search-params (source of truth),
 * mirrors values to localStorage for cross-session persistence, and exposes a
 * simple read/write API.
 *
 * Data-flow (one-directional, no circular effects):
 *
 *   URL  ─── useSearchParams ──▶  hook returns values  ──▶  page renders
 *    ▲                                                           │
 *    └───────── setFilter / applyConfig ◄────────────────────────┘
 */
export function useUrlFilters(filterDefs: FilterDef[], componentPage?: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initSynced = useRef(false);
  const dispatch = useDispatch();

  // Check if any saved view is active (used to freeze preViewFilters sync)
  const activeViewId = useSelector((state: any) => state.views?.activeViewId) as number | null;

  /* ─── Read ─── */
  const filters: FilterValues = useMemo(() => {
    const result: FilterValues = {};
    for (const def of filterDefs) {
      const urlValue = searchParams.get(def.urlKey);
      if (def.multi) {
        if (urlValue !== null) {
          // URL param exists (even if empty) — use it as source of truth
          result[def.urlKey] = urlValue ? urlValue.split(',').filter(Boolean) : [];
        } else {
          // Fallback to localStorage
          const stored = localStorage.getItem(def.storageKey);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              result[def.urlKey] = Array.isArray(parsed) ? parsed : [];
            } catch {
              result[def.urlKey] = [];
            }
          } else {
            result[def.urlKey] = def.defaultValue ? def.defaultValue.split(',').filter(Boolean) : [];
          }
        }
      } else {
        const stored = localStorage.getItem(def.storageKey);
        if (urlValue !== null) {
          result[def.urlKey] = urlValue;
        } else if (stored !== null) {
          result[def.urlKey] = stored;
        } else {
          result[def.urlKey] = def.defaultValue;
        }
      }
    }
    return result;
    // searchParams is the reactive dependency — new object ref on URL change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ─── Sync hook evaluation up to URL on mount ─── */
  useEffect(() => {
    if (initSynced.current) return;
    initSynced.current = true;

    // Build params that match the resolved filter states
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      let needsUpdate = false;

      for (const def of filterDefs) {
        const urlVal = prev.get(def.urlKey);
        const calcVal = filters[def.urlKey];

        // Format calculated value to string for comparison
        let normalizedCalcVal = '';
        if (def.multi && Array.isArray(calcVal)) {
          normalizedCalcVal = calcVal.join(',');
        } else if (!def.multi && typeof calcVal === 'string') {
          normalizedCalcVal = calcVal;
        }

        // Only enforce syncing if the calculated value isn't empty, 
        // to avoid spamming the URL with ?layout=list&type=all when genuinely clean.
        // However, if the user deliberately saved a filter to local storage, we MUST sync it 
        // to the URL so the application state respects it immediately.
        if (normalizedCalcVal) {
          if (urlVal !== normalizedCalcVal) {
            next.set(def.urlKey, normalizedCalcVal);
            needsUpdate = true;
          }
        } else if (urlVal) {
          // If the URL has a value but our calculated filters say it should be empty
          // (e.g. they cleared it), we must delete it from the URL.
          next.delete(def.urlKey);
          needsUpdate = true;
        }
      }

      return needsUpdate ? next : prev;
    }, { replace: true });
  }, [filters, filterDefs, setSearchParams]);

  /* ─── Auto-sync filters to Redux for saved view pre-view state ─── */
  useEffect(() => {
    if (!componentPage || !initSynced.current) return;
    // Don't write when ANY saved view is active — this freezes preViewFilters
    // for all pages, preventing saved view values from leaking into pre-view state
    if (activeViewId != null) return;

    // Convert filters to Record<string, string[]> (same shape as View.config)
    const config: Record<string, string[]> = {};
    for (const def of filterDefs) {
      const val = filters[def.urlKey];
      if (Array.isArray(val)) {
        config[def.urlKey] = val;
      } else if (typeof val === 'string' && val) {
        config[def.urlKey] = [val];
      } else {
        config[def.urlKey] = [];
      }
    }
    dispatch(setPreViewFilters({ component: componentPage, config }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, componentPage, activeViewId]);

  /* ─── Write single filter ─── */
  const setFilter = useCallback(
    (key: string, value: string | string[]) => {
      // Find the def
      const def = filterDefs.find((d) => d.urlKey === key);
      if (!def) return;

      // Persist to localStorage
      if (def.multi && Array.isArray(value)) {
        localStorage.setItem(def.storageKey, JSON.stringify(value));
      } else {
        localStorage.setItem(def.storageKey, String(value));
      }

      // Update URL — merge with existing params
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const strVal = Array.isArray(value) ? value.join(',') : String(value);
        if (strVal) {
          next.set(key, strVal);
        } else {
          next.delete(key);
        }
        return next;
      }, { replace: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterDefs, setSearchParams],
  );

  /* ─── Write multiple filters at once ─── */
  const setFilters = useCallback(
    (updates: Record<string, string | string[]>) => {
      // First persist to localStorage
      for (const [key, value] of Object.entries(updates)) {
        const def = filterDefs.find((d) => d.urlKey === key);
        if (def) {
          if (def.multi && Array.isArray(value)) {
            localStorage.setItem(def.storageKey, JSON.stringify(value));
          } else {
            localStorage.setItem(def.storageKey, String(value));
          }
        }
      }

      // Then update URL in one pass
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          const def = filterDefs.find((d) => d.urlKey === key);
          if (def) {
            const strVal = Array.isArray(value) ? value.join(',') : String(value);
            if (strVal) {
              next.set(key, strVal);
            } else {
              next.delete(key);
            }
          }
        }
        return next;
      }, { replace: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterDefs, setSearchParams]
  );

  /* ─── Bulk-apply from saved view config ─── */
  const applyConfig = useCallback(
    (config: Record<string, string[]>) => {
      const next = new URLSearchParams();

      for (const def of filterDefs) {
        const vals = config[def.urlKey];
        // Persist to localStorage
        if (def.multi) {
          localStorage.setItem(def.storageKey, JSON.stringify(vals || []));
        } else {
          localStorage.setItem(def.storageKey, vals?.[0] ?? def.defaultValue);
        }
        // Set URL param (skip defaults)
        if (vals && vals.length > 0) {
          const strVal = vals.join(',');
          if (strVal) {
            next.set(def.urlKey, strVal);
          }
        }
      }

      setSearchParams(next, { replace: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterDefs, setSearchParams],
  );

  return { filters, setFilter, setFilters, applyConfig };
}
